/**
 * `/` for a visitor (docs/SPEC.md §3, §14.6): what TrueReel is — a description becomes a
 * film — before anyone is asked to sign in. The app proper lives at `/ads` and below. A
 * signed-in visitor sees "Open your ads" where a stranger sees "Sign in"; nothing here
 * needs a session.
 *
 * Neutral by rule (§14.6, owner 2026-10-14): the example is a fictional product,
 * Brightwell, a steel bottle (the starter film's), never a real company and never an
 * Intelibo product. Its pictures are objects of Objects (their published `thumb.jpg`,
 * real TrueReel renders on downloads.intelibo.com) with the captions typeset by the page,
 * as the film typesets them; a showcase rendered from a starter template replaces them
 * once GPU rendering is back (docs/PENDING-HUMAN.md).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ACCOUNTS_WEB_URL } from "../api/client";
import { useMe } from "../api/me";
import { loginUrl } from "../pages/LoginPage";
import "./home.css";

const MEDIA = "https://downloads.intelibo.com/truereel/showcase";
export const SHOWCASE = {
  /** Six curated shapes of Objects: each one's published `thumb.jpg`. */
  objects: [
    {
      src: `${MEDIA}/object-phone-modern-fd79cbe81b.jpg`,
      name: "Modern phone",
    },
    {
      src: `${MEDIA}/object-laptop-modern-5b6305ea1d.jpg`,
      name: "Modern laptop",
    },
    { src: `${MEDIA}/object-can-drink-84d4e24e36.jpg`, name: "Drink can" },
    {
      src: `${MEDIA}/object-bottle-cylinder-890f72e833.jpg`,
      name: "Cylinder bottle",
    },
    {
      src: `${MEDIA}/object-armchair-modern-38a993b4e2.jpg`,
      name: "Modern armchair",
    },
    {
      src: `${MEDIA}/object-plant-potted-large-73d09da6f8.jpg`,
      name: "Large potted plant",
    },
  ],
};
const BOTTLE = SHOWCASE.objects[3].src;
const PLANT = SHOWCASE.objects[5].src;

/** The example film's scenes, as its scene strip shows them. */
export const EXAMPLE_SCENES = [
  { id: "s1", picture: BOTTLE, caption: "Cold for 24 hours." },
  { id: "s2", picture: PLANT, caption: "From the desk to the trail." },
  { id: "s3", picture: BOTTLE, caption: "Brightwell" },
];

/** The three ways out of an approved film (§13), with their prices (§15). */
export const WAYS_OUT = [
  {
    title: "The real-time film",
    blurb:
      "The player's own engine renders the film on our servers at 720p or 1080p. The quickest and the cheapest.",
    when: "seconds to a minute",
    price: "free on our CPU servers; GPU seconds when it runs on a GPU",
  },
  {
    title: "The ray-traced final",
    blurb:
      "Every frame ray-traced on the render farm from the same description. See one moment for real first.",
    when: "minutes",
    price: "metered GPU seconds, shown before you start",
  },
  {
    title: "A description to take elsewhere",
    blurb:
      "The film written out scene by scene, with a picture of each scene, to paste into the generative video tool you prefer.",
    when: "at once",
    price: "free",
  },
];

/** Credit packs as registered in Intelibo Accounts. A film here is §15.1's
 *  measured ad to its first film (about 1,150 credits at the default margin)
 *  and five changes (about 180 each): about 2,050 credits. */
export const PACKS = [
  {
    name: "Starter",
    price: "$5",
    credits: "12,500 credits",
    note: "About six films, each with five changes.",
  },
  {
    name: "Standard",
    price: "$10",
    credits: "27,500 credits",
    note: "About thirteen films, each with five changes.",
    featured: true,
  },
  {
    name: "Pro",
    price: "$25",
    credits: "75,000 credits",
    note: "About thirty-five films, or ray-traced finals.",
  },
];

/** What things cost (§15.1): credits pay the service's cost — the AI model's
 *  tokens and the GPU's seconds — with one margin; CPU work is free. */
export const PRICES = [
  {
    what: "The conversation, the film's writing and each change you say",
    price: "what the AI model's tokens cost, with our margin",
  },
  { what: "An ad to its first film, typically", price: "about 1,150 credits" },
  { what: "A change you say, typically", price: "about 180 credits" },
  {
    what: "A replacement, Undo, going back to a version",
    price: "free",
  },
  {
    what: "Building and playing the scenes, looking around, the description to take elsewhere",
    price: "free (our CPU servers)",
  },
  {
    what: "The real-time film",
    price: "free on our CPU servers; metered GPU seconds on a GPU",
  },
  {
    what: "The ray-traced final, a true render of a moment",
    price: "metered GPU seconds, with our margin",
  },
];

/** Marks `.reveal` elements as they scroll into view; a no-op without the observer. */
function useReveal(root: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const host = root.current;
    if (!host) return;
    const items = Array.from(host.querySelectorAll<HTMLElement>(".reveal"));
    if (typeof IntersectionObserver === "undefined") {
      for (const el of items) el.classList.add("in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add("in");
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 },
    );
    for (const el of items) io.observe(el);
    return () => io.disconnect();
  }, [root]);
}

function useSolidNav(): boolean {
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return solid;
}

/** One moment of the example film: an object of Objects on its stage, the
 *  scene's caption typeset over it. */
function Moment({
  picture,
  caption,
  label,
  small = false,
}: {
  picture: string;
  caption: string;
  label?: string;
  small?: boolean;
}) {
  return (
    <div
      className={`moment${small ? " small" : ""}`}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <img src={picture} alt="" loading="lazy" />
      <span className="moment-caption">{caption}</span>
    </div>
  );
}

export function HomePage() {
  const root = useRef<HTMLDivElement>(null);
  useReveal(root);
  const solid = useSolidNav();
  const me = useMe();
  const signedIn = Boolean(me.data);
  const startHref = signedIn ? "/new" : loginUrl("/new");
  const openHref = signedIn ? "/ads" : loginUrl("/ads");
  // a build variable of the web app, unset by default (the band of Objects)
  const objectsBand = import.meta.env.VITE_HOME_3D_LIBRARY === "true";

  const start = (testId?: string) =>
    signedIn ? (
      <Link className="btn btn-primary" to="/new" data-testid={testId}>
        Make your first ad <span aria-hidden="true">→</span>
      </Link>
    ) : (
      <a className="btn btn-primary" href={startHref} data-testid={testId}>
        Make your first ad <span aria-hidden="true">→</span>
      </a>
    );

  return (
    <div className="home" ref={root} data-testid="home">
      <header className={`home-nav${solid ? " solid" : ""}`}>
        <div className="home-nav-inner">
          <Link to="/" aria-label="TrueReel home" className="flex items-center">
            <img
              src="/brand/logo/truereel-lockup-on-dark.svg"
              alt="TrueReel, by Intelibo"
              className="h-8 w-auto"
            />
          </Link>
          <nav className="home-nav-links" aria-label="Sections">
            <a href="#how">How it works</a>
            <a href="#description">Why a description</a>
            <a href="#talk">Talk with the film</a>
            <a href="#out">Three ways out</a>
            <a href="#pricing">Pricing</a>
          </nav>
          {signedIn ? (
            <Link className="btn btn-nav" to="/ads" data-testid="nav-cta">
              Open your ads
            </Link>
          ) : (
            <a className="btn btn-nav" href={openHref} data-testid="nav-cta">
              Sign in
            </a>
          )}
        </div>
      </header>

      <main>
        {/* ------------------------------------------------------------ hero */}
        <section className="hero">
          <div className="aurora" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
          <div className="sprockets" aria-hidden="true" />
          <div className="hero-inner">
            <div>
              <p className="kicker-pill">
                <span className="dot" />
                Ads made from words, not guessed pixels
              </p>
              <h1>
                A description
                <br />
                <span className="grad">becomes a film.</span>
              </h1>
              <p className="lead">
                Say what you want: a sentence, a web address, a few photos. A
                short conversation turns it into a brief, and the brief into a
                film that plays in your browser within seconds. Then change it
                by saying so.
              </p>
              <div className="ctas">
                {start("hero-cta")}
                <a className="btn btn-ghost" href="#how">
                  See how it works
                </a>
              </div>
              <ul className="facts" aria-label="At a glance">
                <li>
                  <b>3 questions</b>
                  <span>at most, before the film</span>
                </li>
                <li>
                  <b>Seconds</b>
                  <span>to a film that plays</span>
                </li>
                <li>
                  <b>Every letter</b>
                  <span>typeset, never diffused</span>
                </li>
                <li>
                  <b>#1f5f8b</b>
                  <span>your colours, to the hex</span>
                </li>
              </ul>
            </div>
            <div className="hero-stage">
              <div className="phone">
                <div className="phone-screen">
                  <Moment
                    picture={BOTTLE}
                    caption="Cold for 24 hours."
                    label="A moment of a film for Brightwell, a fictional steel bottle: the bottle on a grey stage, the caption typeset over it"
                  />
                </div>
                <div className="frame-badge" aria-hidden="true">
                  <b>scene 1 of 3 · 1.5 s</b>
                  the same moment, every time
                </div>
              </div>
              <p className="live-note">
                <span className="pulse" />A bottle from Objects, its caption
                typeset. Brightwell is made up.
              </p>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ how */}
        <section className="band light" id="how">
          <div className="wrap">
            <div className="section-head reveal">
              <p className="kicker">How it works</p>
              <h2>Say it, answer a question or two, watch the film.</h2>
              <p className="sub">
                Nothing before the film touches a GPU; the conversation costs
                only what the AI uses. Every step is one page, the ad&apos;s
                own.
              </p>
            </div>
            <ol className="steps">
              <li className="step reveal">
                <span className="num">1</span>
                <div className="art brief" aria-hidden="true">
                  <span className="ph">What is the ad about?</span>
                  <br />
                  An ad for <mark>Brightwell</mark>, a steel bottle that keeps
                  water cold for <mark>24 hours</mark>. Calm, outdoors.
                  <br />
                  <br />
                  <span className="ph">
                    brightwell.example · logo found · 3 colours · 9:16 · 15 s
                  </span>
                </div>
                <span className="when">a few seconds</span>
                <h3>Say what you want</h3>
                <p>
                  One box. A sentence, a web address, product photos, a
                  spreadsheet of numbers, or nothing more than &ldquo;an ad for
                  my product&rdquo;.
                </p>
              </li>
              <li className="step reveal">
                <span className="num">2</span>
                <div className="art chat" aria-hidden="true">
                  <p className="bubble">Who is it for?</p>
                  <p className="choices">
                    <span className="chosen">Hikers</span>
                    <span>Commuters</span>
                    <span>You decide</span>
                  </p>
                  <div className="mini-board">
                    {EXAMPLE_SCENES.map((s) => (
                      <Moment
                        key={s.id}
                        picture={s.picture}
                        caption={s.caption}
                        small
                      />
                    ))}
                  </div>
                </div>
                <span className="when">the AI's tokens</span>
                <h3>A short conversation</h3>
                <p>
                  At most three questions, each with an answer already chosen.
                  Then the brief, as a card you can read and edit, and the film:
                  each scene plays as soon as it is built.
                </p>
              </li>
              <li className="step reveal">
                <span className="num">3</span>
                <div className="art film" aria-hidden="true">
                  <Moment
                    picture={PLANT}
                    caption="From the desk to the trail."
                  />
                  <span className="scrub">
                    <i style={{ width: "42%" }} />
                  </span>
                </div>
                <span className="when">seconds · the AI's tokens</span>
                <h3>The film, in your browser</h3>
                <p>
                  The brief becomes the film, each scene playing as soon as it
                  is built: every caption, at your aspect. Pause on any moment;
                  it is the same moment every time.
                </p>
              </li>
            </ol>
          </div>
        </section>

        {/* ------------------------------------------------------------ description */}
        <section className="band dark" id="description">
          <div className="wrap">
            <div className="section-head reveal">
              <p className="kicker">Why a description</p>
              <h2>Other tools guess pixels. TrueReel writes a description.</h2>
              <p className="sub">
                Every film is data a person can read, a program can check and a
                sentence can change: its scenes, its objects, its light, its
                camera and its words. Everything a brand needs to be right
                follows from that.
              </p>
            </div>
            <div className="proof reveal">
              <div className="code-window">
                <div className="cw-bar">
                  <i />
                  <i />
                  <i />
                  <span>film.json · scene 1</span>
                </div>
                <pre>
                  <code>
                    {"{ "}
                    <span className="k">&quot;id&quot;</span>:{" "}
                    <span className="s">&quot;s1&quot;</span>,{" "}
                    <span className="k">&quot;seconds&quot;</span>:{" "}
                    <span className="n">4.0</span>,{"\n  "}
                    <span className="k">&quot;stage&quot;</span>: {"{ "}
                    <span className="k">&quot;rig&quot;</span>:{" "}
                    <span className="s">&quot;studio-white&quot;</span>
                    {" },\n  "}
                    <span className="k">&quot;objects&quot;</span>: [{"{ "}
                    <span className="k">&quot;ref&quot;</span>:{" "}
                    <span className="s">&quot;bottle&quot;</span>,{" "}
                    <span className="c">
                      {"// steel-bottle@1, from Objects"}
                    </span>
                    {"\n    "}
                    <span className="k">&quot;motion&quot;</span>: {"{ "}
                    <span className="k">&quot;rig&quot;</span>:{" "}
                    <span className="s">&quot;turntable&quot;</span>,{" "}
                    <span className="k">&quot;degrees&quot;</span>:{" "}
                    <span className="n">40</span>
                    {" } }],\n  "}
                    <span className="k">&quot;camera&quot;</span>: {"{ "}
                    <span className="k">&quot;move&quot;</span>:{" "}
                    <span className="s">&quot;push-in&quot;</span>
                    {" },\n  "}
                    <span className="k">&quot;captions&quot;</span>: [{"{ "}
                    <span className="k">&quot;text&quot;</span>:{" "}
                    <span className="s">
                      &quot;Cold for {"{hours}"} hours.&quot;
                    </span>
                    ,{"\n                 "}
                    <span className="k">&quot;color&quot;</span>:{" "}
                    <span className="s">&quot;#1f5f8b&quot;</span>
                    {" }] }"}
                  </code>
                </pre>
              </div>
              <figure className="proof-frame">
                <Moment
                  picture={BOTTLE}
                  caption="Cold for 24 hours."
                  label="The moment the lines on the left describe: the bottle, its caption typeset"
                />
                <figcaption>
                  The scene on the left, at 1.5 s. The same description draws
                  the same picture next year; a new aspect ratio is a re-render.
                </figcaption>
              </figure>
            </div>
            <div className="cards4">
              <article className="feature reveal">
                <div className="ico">Aa</div>
                <h3>Text is spelled right</h3>
                <p>
                  Captions, prices and labels are typeset by our code, never
                  dreamt up by a model that cannot read.
                </p>
              </article>
              <article className="feature reveal">
                <div className="ico">#</div>
                <h3>Colours are exact</h3>
                <p>
                  Your brand kit&apos;s hex values go into the description as
                  values. Your blue is your blue.
                </p>
              </article>
              <article className="feature reveal">
                <div className="ico">Σ</div>
                <h3>Numbers are true</h3>
                <p>
                  A price or a figure comes from your site or your spreadsheet,
                  so the film cannot drift from the product.
                </p>
              </article>
              <article className="feature reveal">
                <div className="ico">⟲</div>
                <h3>Any moment, again</h3>
                <p>
                  Same description, same engine, same moment: same pixels. A new
                  shape is a re-render, not a new roll of the dice.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ objects */}
        {objectsBand && (
          <section className="band light" id="objects">
            <div className="wrap split">
              <div className="reveal">
                <p className="kicker">Objects</p>
                <h2>Real objects, at their real size.</h2>
                <p className="sub">
                  A phone is 14.7 cm tall and its screen takes your screenshot.
                  A can has a label that wraps around it and says exactly what
                  you wrote. TrueReel places ready-made objects in your film,
                  and a simple stand-in for what Objects does not have yet.
                </p>
                <ul className="ticks">
                  <li>
                    <b>Your screen, your label.</b> Attach a picture or let
                    TrueReel typeset the words. Nothing is guessed.
                  </li>
                  <li>
                    <b>Your colours.</b> Brand colours go on as hex values, on
                    real materials.
                  </li>
                  <li>
                    <b>Yours to reuse.</b> Dress an object once, save the look,
                    use it in every ad.
                  </li>
                </ul>
              </div>
              <div className="reveal">
                <div className="objects">
                  {SHOWCASE.objects.map((o) => (
                    <img key={o.src} src={o.src} alt={o.name} loading="lazy" />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------ talk */}
        <section className="band" id="talk">
          <div className="wrap split">
            <div className="reveal">
              <p className="kicker">Talk with the film</p>
              <h2>Change one thing. Keep everything else.</h2>
              <p className="sub">
                You talk about what is in front of you: the scene, the paused
                moment, the object you clicked, the area you marked. One
                sentence changes one thing in the description.
              </p>
              <ul className="ticks">
                <li>
                  <b>Say it.</b> &ldquo;Make the cap black.&rdquo; &ldquo;Slower
                  at the start.&rdquo; &ldquo;Swap the plant for a lamp.&rdquo;
                  Most changes play at once and cost nothing.
                </li>
                <li>
                  <b>Point at it.</b> Pause, click the object or mark the area,
                  and say it about that.
                </li>
                <li>
                  <b>Lock what is right.</b> A scene or an object you approved
                  is never changed again.
                </li>
                <li>
                  <b>Go back.</b> Every change is a version; any of them plays
                  again. A change that costs says what before it starts.
                </li>
                <li>
                  <b>Share a review link.</b> Colleagues watch and leave timed
                  comments without an account.
                </li>
              </ul>
            </div>
            <div className="reveal">
              <div className="talk" aria-hidden="true">
                <Moment picture={BOTTLE} caption="Cold for 24 hours." />
                <p className="bubble you">Make the cap black.</p>
                <p className="bubble">
                  The cap is black in every scene. Version 4 · free
                </p>
                <p className="bubble you">Slower at the start.</p>
                <p className="bubble">
                  Scene 1 is 5.0 s now, the push-in eased. Version 5 · free
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ ways out */}
        <section className="band light" id="out">
          <div className="wrap">
            <div className="section-head reveal">
              <p className="kicker">Approve</p>
              <h2>Three ways out of one description.</h2>
              <p className="sub">
                When the film is right, approve it and take it out the way you
                need it: take all three if you like. It stays editable.
              </p>
            </div>
            <div className="types">
              {WAYS_OUT.map((w) => (
                <div key={w.title} className="type reveal">
                  <h3>{w.title}</h3>
                  <p>{w.blurb}</p>
                  <small>
                    {w.when} · {w.price}
                  </small>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ pricing */}
        <section className="band" id="pricing">
          <div className="wrap">
            <div className="section-head reveal">
              <p className="kicker">Pricing</p>
              <h2>Pay for the AI and the GPU time. Nothing else.</h2>
              <p className="sub">
                Credits pay what the service costs: the AI model&apos;s tokens
                and the GPU&apos;s seconds, with one margin on top. Work on our
                CPU servers is free. One credit is one second on our reference
                GPU. You are asked first before anything costly, an action your
                credits cannot cover does not start, and our failures cost you
                nothing.
              </p>
            </div>
            <div className="ledger reveal" aria-label="What things cost">
              <table>
                <tbody>
                  {PRICES.map((p) => (
                    <tr key={p.what}>
                      <td>{p.what}</td>
                      <td>{p.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="plans">
              {PACKS.map((p) => (
                <div
                  key={p.name}
                  className={`plan reveal${p.featured ? " featured" : ""}`}
                >
                  <h3>{p.name}</h3>
                  <div className="price">{p.price}</div>
                  <div className="credits">{p.credits}</div>
                  <small>{p.note}</small>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-sm text-slate-500 reveal">
              Every new account starts with a grant that covers its first films.
              Credits are bought and billed through{" "}
              <a href={ACCOUNTS_WEB_URL} className="text-brand">
                Intelibo Accounts
              </a>
              , shared with every Intelibo product.
            </p>
          </div>
        </section>

        {/* ------------------------------------------------------------ closing */}
        <section className="closing">
          <div className="aurora" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
          <div className="wrap reveal">
            <h2>Your next film is a sentence away.</h2>
            <p className="sub">
              A film in your browser in seconds. A ray-traced final when you say
              so.
            </p>
            <div className="ctas center">
              {start()}
              <a className="btn btn-ghost" href="#how">
                How it works
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="wrap footer-inner">
          <div>
            <img
              src="/brand/logo/truereel-lockup-on-dark.svg"
              alt="TrueReel, by Intelibo"
              className="h-7 w-auto"
            />
            <p className="mt-2 text-xs">by Intelibo · © 2026</p>
          </div>
          <nav className="footer-links" aria-label="Footer">
            <div>
              <b>Product</b>
              <a href="#how">How it works</a>
              <a href="#description">Why a description</a>
              <a href="#talk">Talk with the film</a>
              <a href="#pricing">Pricing</a>
            </div>
            <div>
              <b>Account</b>
              <a href={openHref}>Your ads</a>
              <a href={signedIn ? "/media" : loginUrl("/media")}>Media</a>
              <a href={`${ACCOUNTS_WEB_URL}/account`}>Credits and receipts</a>
            </div>
            <div>
              <b>Intelibo</b>
              <a href={ACCOUNTS_WEB_URL}>Accounts</a>
              <a href="https://downloads.intelibo.com/truereel/brand.json">
                Brand kit
              </a>
            </div>
          </nav>
        </div>
        <p className="wrap credit">
          Shown: objects of Objects rendered by TrueReel, with captions typeset
          by this page. Brightwell is a made-up product.
        </p>
      </footer>
    </div>
  );
}
